import type { World } from '../../shared/types.js';
import { ROAD_SPECS } from '../../shared/osm/classify.js';
import { distToSegment2 } from '../../shared/geom.js';

/**
 * Which street the car is on, by name, for the sign that slides in when it changes.
 *
 * The names were always in the world — `worldgen` keeps OSM's `name` on every road — and
 * nearly every drivable way has one: 431 of 443 in Kraków's Old Town, 328 of 332 in
 * Midtown. What is missing is mostly footpaths and driveways, where saying nothing is the
 * right answer anyway.
 *
 * A grid of its own rather than a question to `SurfaceIndex`: that one answers "how fast
 * can you go here" in the physics' hot loop, and should not grow a string per segment and
 * an alignment score for a sign that changes a few times a minute.
 */

const CELL = 32;

/**
 * Metres either side of the carriageway that still count as being on it. A car clipping
 * the kerb on a corner has not left the street it is cornering on.
 */
const SLACK = 2.5;

interface NamedSegment {
  ax: number;
  az: number;
  bx: number;
  bz: number;
  /** Unit direction, for telling the street you are driving along from the one you are crossing. */
  ux: number;
  uz: number;
  reach2: number;
  name: string;
}

function cellKey(cx: number, cz: number): number {
  return ((cx + 32768) << 16) | (cz + 32768);
}

export class StreetIndex {
  private readonly segments: NamedSegment[] = [];
  private readonly grid = new Map<number, number[]>();

  constructor(world: Pick<World, 'roads'>) {
    for (const road of world.roads) {
      const name = road.name?.trim();
      if (!name || !ROAD_SPECS[road.cls].drivable) continue;
      const reach = road.width / 2 + SLACK;
      for (let i = 2; i < road.pts.length; i += 2) {
        const ax = road.pts[i - 2];
        const az = road.pts[i - 1];
        const bx = road.pts[i];
        const bz = road.pts[i + 1];
        const length = Math.hypot(bx - ax, bz - az);
        if (length < 1e-6) continue;
        const index = this.segments.length;
        this.segments.push({ ax, az, bx, bz, ux: (bx - ax) / length, uz: (bz - az) / length, reach2: reach * reach, name });
        const minX = Math.floor((Math.min(ax, bx) - reach) / CELL);
        const maxX = Math.floor((Math.max(ax, bx) + reach) / CELL);
        const minZ = Math.floor((Math.min(az, bz) - reach) / CELL);
        const maxZ = Math.floor((Math.max(az, bz) + reach) / CELL);
        for (let cx = minX; cx <= maxX; cx++) {
          for (let cz = minZ; cz <= maxZ; cz++) {
            const key = cellKey(cx, cz);
            const bucket = this.grid.get(key);
            if (bucket) bucket.push(index);
            else this.grid.set(key, [index]);
          }
        }
      }
    }
  }

  /**
   * The street under a point, or null where no named road is.
   *
   * In a junction the car is on two streets at once, and the one it is on is the one it is
   * pointing along — so overlapping candidates are ranked by how parallel they are to the
   * heading, and only then by distance. Which way along the street does not matter: a car
   * driving down Floriańska the wrong way is still on Floriańska.
   */
  at(x: number, z: number, heading: number): string | null {
    const bucket = this.grid.get(cellKey(Math.floor(x / CELL), Math.floor(z / CELL)));
    if (!bucket) return null;
    const hx = Math.sin(heading);
    const hz = Math.cos(heading);
    let best: NamedSegment | null = null;
    let bestScore = -Infinity;
    for (const index of bucket) {
      const s = this.segments[index];
      const { d2 } = distToSegment2(x, z, s.ax, s.az, s.bx, s.bz);
      if (d2 > s.reach2) continue;
      // Alignment dominates; distance only separates two equally parallel candidates, such
      // as the two carriageways of a dual road that OSM names separately.
      const score = Math.abs(hx * s.ux + hz * s.uz) * 10 - Math.sqrt(d2 / s.reach2);
      if (score > bestScore) {
        bestScore = score;
        best = s;
      }
    }
    return best?.name ?? null;
  }
}

/**
 * Metres a new street has to hold before it is announced.
 *
 * Long enough that cutting across the corner of a side street, or the half-second a
 * junction spends deciding which road you are on, never puts a sign up — and short enough
 * that the sign still arrives while the turn you just made is on screen.
 */
export const SETTLE_METRES = 25;

/**
 * Turns the street under the car, sampled as it drives, into the moments worth a sign.
 *
 * A name is announced once it has been under the car for `SETTLE_METRES` and is not the
 * one announced last. Stretches with no name — a car park, the verge, an unnamed link —
 * neither announce anything nor forget the current street, so leaving Floriańska for the
 * pavement and coming back is not a second "Floriańska".
 */
export class StreetTracker {
  private announced: string | null = null;
  private candidate: string | null = null;
  private held = 0;

  /**
   * The street last announced: the one under the minimap. Kept over an unnamed driveway or a
   * square, where the car has not left the street so much as stepped off it.
   */
  get current(): string | null {
    return this.announced;
  }

  /** The street to announce now, or null if nothing changed. */
  update(name: string | null, metres: number): string | null {
    if (name === null || name === this.announced) {
      this.candidate = null;
      this.held = 0;
      return null;
    }
    if (name !== this.candidate) {
      this.candidate = name;
      this.held = 0;
    }
    this.held += metres;
    if (this.held < SETTLE_METRES) return null;
    this.announced = name;
    this.candidate = null;
    this.held = 0;
    return name;
  }

  reset(): void {
    this.announced = null;
    this.candidate = null;
    this.held = 0;
  }
}
